import DOMPurify from 'dompurify';
import type { Config } from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param html The HTML content to sanitize
 * @param options Custom DOMPurify configuration options
 * @returns Sanitized HTML string
 */
export function sanitizeHtml(html: string, options: Config = {}): string {
    // 如果提供了自定义配置，则完全使用自定义配置
    if (Object.keys(options).length > 0) {
        return DOMPurify.sanitize(html, options).toString();
    }

    // 否则使用默认配置
    const defaultOptions: Config = {
        ALLOWED_TAGS: ['p', 'b', 'i', 'em', 'strong', 'span', 'br', 'div', 'a'],
        ALLOWED_ATTR: ['class', 'style', 'id', 'href'],
        KEEP_CONTENT: true, // 保持文本内容完整
        // 支持自定义元素
        CUSTOM_ELEMENT_HANDLING: {
            tagNameCheck: (tagName) => {
                // 允许以 'pt-' 或 'patent-' 开头的自定义元素
                return /^(pt-|patent-)/.test(tagName);
            },
            attributeNameCheck: (attr) => {
                // 允许自定义元素的特定属性
                return /^(term-|data-patent-|patent-)/.test(attr);
            },
            allowCustomizedBuiltInElements: true, // 允许自定义内置元素 (如 <a is="patent-link">)
        },
    };

    return DOMPurify.sanitize(html, defaultOptions);
}

/**
 * Vue directive for safely rendering HTML content
 * Usage: v-safe-html="content"
 */
export const vSafeHtml = {
    mounted(el: HTMLElement, binding: { value: string }) {
        const sanitized = sanitizeHtml(binding.value);
        el.innerHTML = sanitized;
        // Store the last sanitized content to avoid unnecessary updates
        (el as any).__lastSafeHtml = sanitized;
    },
    updated(el: HTMLElement, binding: { value: string, oldValue: string }) {
        // Only update if the value actually changed
        if (binding.value !== binding.oldValue) {
            const sanitized = sanitizeHtml(binding.value);
            const lastContent = (el as any).__lastSafeHtml;

            // Only update DOM if sanitized content is different
            // This preserves text selection when content hasn't actually changed
            if (sanitized !== lastContent) {
                // Check if user has text selected to minimize disruption
                const selection = window.getSelection();
                const hasSelection = selection && selection.toString().length > 0;

                if (!hasSelection) {
                    // Safe to update when no text is selected
                    el.innerHTML = sanitized;
                    (el as any).__lastSafeHtml = sanitized;
                }
                // If user has text selected, defer update or use more gentle approach
                else {
                    // Schedule update after current selection is likely to be cleared
                    setTimeout(() => {
                        // Re-check if selection still exists
                        const currentSelection = window.getSelection();
                        if (!currentSelection || currentSelection.toString().length === 0) {
                            el.innerHTML = sanitized;
                            (el as any).__lastSafeHtml = sanitized;
                        }
                    }, 100);
                }
            }
        }
    },
    beforeUnmount(el: HTMLElement) {
        // Clean up stored data
        delete (el as any).__lastSafeHtml;
    },
};
